import React from 'react';
import { Box, Skeleton, useTheme } from '@mui/material';

export const NewsCardSkeleton = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        overflow: 'hidden',
        mb: 1.5,
      }}
    >
      <Skeleton variant="rectangular" sx={{ width: { xs: '100%', md: 200 }, height: { xs: 160, md: 'auto' }, minHeight: { md: 140 }, flexShrink: 0 }} />
      <Box sx={{ flex: 1, p: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '6px' }} />
          <Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: '6px' }} />
        </Box>
        <Skeleton variant="text" width="85%" height={22} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="60%" height={22} sx={{ mb: 1.5 }} />
        <Skeleton variant="text" width="100%" height={18} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="75%" height={18} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Skeleton variant="text" width={140} height={16} />
          <Skeleton variant="rounded" width={90} height={28} sx={{ borderRadius: '6px' }} />
        </Box>
      </Box>
    </Box>
  );
};

export const CategoryFilterSkeleton = () => {
  const theme = useTheme();
  return (
    <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, p: 2 }}>
      <Skeleton variant="text" width="50%" height={20} sx={{ mb: 2 }} />
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} variant="rounded" height={34} sx={{ mb: 0.5, borderRadius: '8px' }} />
      ))}
    </Box>
  );
};

export const RecommendationsSkeleton = () => {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Skeleton variant="circular" width={16} height={16} />
        <Skeleton variant="text" width={120} height={20} />
      </Box>
      {[...Array(4)].map((_, i) => (
        <Box key={i} sx={{ py: 1.5 }}>
          <Skeleton variant="text" width="95%" height={18} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="65%" height={14} />
        </Box>
      ))}
    </Box>
  );
};

export const NewsDetailSkeleton = () => {
  const theme = useTheme();
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
        <Skeleton variant="text" width={150} height={24} />
      </Box>
      <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Skeleton variant="rectangular" height={280} sx={{ width: '100%' }} />
        <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rounded" width={120} height={24} sx={{ borderRadius: '6px' }} />
          </Box>
          <Skeleton variant="text" width="80%" height={36} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="55%" height={36} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Skeleton variant="text" width={100} height={18} />
            <Skeleton variant="text" width={80} height={18} />
          </Box>
          {[...Array(10)].map((_, i) => (
            <Skeleton key={i} variant="text" width={`${85 + Math.random() * 15}%`} height={18} sx={{ mb: 0.75 }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const PreferencesScreenSkeleton = () => {
  const theme = useTheme();
  return (
    <Box sx={{ px: { xs: 2, sm: 1 }, py: 2.2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1.5 }} />
        <Skeleton variant="text" width={220} height={32} />
      </Box>
      <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, p: { xs: 2.5, sm: 3 } }}>
        <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5, mb: 4 }}>
          {[...Array(9)].map((_, i) => <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: '8px' }} />)}
        </Box>
        <Skeleton variant="text" width="25%" height={24} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
          <Skeleton variant="rounded" height={40} sx={{ flex: 1, borderRadius: '8px' }} />
          <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: '8px' }} />
        </Box>
        <Skeleton variant="text" width="20%" height={24} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={64} sx={{ borderRadius: '10px', mb: 1.5 }} />
        <Skeleton variant="rounded" height={64} sx={{ borderRadius: '10px' }} />
      </Box>
    </Box>
  );
};

export const CategorySkeletonItem = () => (
  <Skeleton variant="rounded" height={44} sx={{ borderRadius: '8px' }} />
);
